/**
 * /flows — list view + new-flow + archive.
 */
import { useEffect, useState } from "react";
import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { api } from "../../../lib/api.js";

interface FlowRow {
  id: string;
  name: string;
  status: "draft" | "published" | "archived";
  publishedVersionId: string | null;
  draftUpdatedAt: string | null;
  updatedAt: string;
}

export const Route = createFileRoute("/_app/flows/")({
  component: FlowsListPage,
});

function FlowsListPage() {
  const navigate = useNavigate();
  const [flows, setFlows] = useState<FlowRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creatingName, setCreatingName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void load();
  }, []);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get<{ flows: FlowRow[] }>("/flows");
      setFlows(r.data.flows);
    } catch {
      setError("Failed to load flows");
    } finally {
      setLoading(false);
    }
  }

  async function createFlow(e: React.FormEvent) {
    e.preventDefault();
    if (!creatingName.trim() || busy) return;
    setBusy(true);
    try {
      const r = await api.post<{ flow: FlowRow }>("/flows", {
        name: creatingName.trim(),
      });
      setCreatingName("");
      void navigate({ to: `/flows/$flowId/edit`, params: { flowId: r.data.flow.id } });
    } catch {
      setError("Failed to create flow");
    } finally {
      setBusy(false);
    }
  }

  async function archive(id: string) {
    if (!confirm("Archive this flow? In-flight runs continue on their pinned version.")) return;
    try {
      await api.delete(`/flows/${id}`);
      setFlows((cur) => cur.filter((f) => f.id !== id));
    } catch {
      setError("Failed to archive");
    }
  }

  return (
    <section>
      <h1 style={{ margin: "0 0 8px", fontSize: 28 }}>Flows</h1>
      <span
        style={{
          display: "inline-block",
          padding: "2px 8px",
          border: "var(--border-hair)",
          borderRadius: 12,
          background: "var(--bg-surface)",
          fontSize: 11,
          color: "var(--text-muted)",
          fontFamily: "var(--font-mono)",
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          marginBottom: 16,
        }}
      >
        Shared across all Instagram accounts
      </span>
      <p style={{ color: "var(--muted)", marginTop: 0, marginBottom: 24 }}>
        Multi-step automations triggered by keywords. Edit, publish, and your
        next inbound DM walks the new graph.
      </p>

      <div style={{ display: "flex", gap: 8, marginBottom: 24, alignItems: "center", flexWrap: "wrap" }}>
        <form
          onSubmit={createFlow}
          style={{ display: "flex", gap: 8, alignItems: "center", flex: 1 }}
        >
          <input
            type="text"
            placeholder="New flow name…"
            value={creatingName}
            onChange={(e) => setCreatingName(e.target.value)}
            className="brut-input"
            style={{ flex: 1, maxWidth: 360 }}
          />
          <button type="submit" disabled={!creatingName.trim() || busy} className="brut-button">
            {busy ? "Creating…" : "New flow"}
          </button>
        </form>
        <Link to="/flows/templates" className="brut-button is-secondary">
          Browse templates
        </Link>
      </div>

      {error ? (
        <div className="brut-card" style={{ background: "#ffd7d7", marginBottom: 16, padding: 12 }}>
          {error}
        </div>
      ) : null}

      {loading ? (
        <p style={{ color: "var(--muted)" }}>Loading…</p>
      ) : flows.length === 0 ? (
        <div className="brut-card" style={{ textAlign: "center", padding: 40 }}>
          <p style={{ margin: 0, fontSize: 15 }}>
            No flows yet. Make one above, then point a trigger keyword at it on
            the auto-replies page.
          </p>
        </div>
      ) : (
        <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 12 }}>
          {flows.map((flow) => (
            <li
              key={flow.id}
              className="brut-card"
              style={{ padding: 16, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16 }}
            >
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>{flow.name}</div>
                <div style={{ fontSize: 12, color: "var(--muted)" }}>
                  <StatusPill status={flow.status} />
                  <span style={{ marginLeft: 12 }}>
                    Updated {formatTime(flow.updatedAt)}
                  </span>
                </div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  onClick={() => navigate({ to: `/flows/$flowId/edit`, params: { flowId: flow.id } })}
                  className="brut-button"
                  style={{ fontSize: 13, padding: "6px 12px" }}
                >
                  Open
                </button>
                <button
                  onClick={() => void archive(flow.id)}
                  className="brut-button is-secondary"
                  style={{ fontSize: 13, padding: "6px 12px" }}
                >
                  Archive
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function StatusPill({ status }: { status: FlowRow["status"] }) {
  const bg = status === "published" ? "#a0e1c2" : status === "draft" ? "#ffe28a" : "#ddd";
  return (
    <span
      style={{
        background: bg,
        border: "2px solid var(--ink)",
        padding: "2px 8px",
        fontSize: 11,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
      }}
    >
      {status}
    </span>
  );
}

function formatTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return "just now";
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.round(ms / 3_600_000)}h ago`;
  return new Date(iso).toLocaleDateString();
}
